/**
 * A gate node on the linkage canvas (Feature 79, revision 1, D25, D26): a
 * diamond with its name over it and its kind under it, if-else gate or switch
 * gate, tied to its condition nodes. Its way in comes from the node it
 * follows, a link dragged into its left side; until it has one it says so
 * (D29).
 *
 * Resting the pointer on it, or the keyboard, opens its card: every condition
 * in order with its conditions in full, or a switch's field and each case's
 * values (D27).
 */
import { Handle, Position, type Node, type NodeProps } from '../../ui/flow.js';
import { HoverCard } from '../../ui/HoverCard.js';
import { useNodeCard } from './hovered.js';
import type { NodeCard } from './node-card.js';
import type { GateFlowNode } from './to-flow.js';

export type GateNodeData = GateFlowNode['data'] & Record<string, unknown>;
export type GateNodeType = Node<GateNodeData, 'gate'>;

const ANCHOR = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 'none' };
const WAY_IN = {
  width: 9,
  height: 9,
  background: 'var(--surface-card)',
  border: '1.5px solid var(--text-soft)',
};

/** The gate's card: what it follows, then its conditions as they are tried. */
function GateCardView({ card }: { card: NodeCard }) {
  return (
    <>
      <span className="mono" style={{ fontWeight: 'var(--weight-semibold)' }}>
        {card.title}
      </span>
      {(card.branches ?? []).map((line) => (
        <span
          key={line.text}
          className="mono"
          style={{
            fontSize: 'var(--text-meta)',
            color: line.faint ? 'var(--text-faint)' : 'var(--text-strong)',
          }}
        >
          {line.text}
        </span>
      ))}
    </>
  );
}

export function GateNode({ id, data, selected }: NodeProps<GateNodeType>) {
  const card = useNodeCard(id);
  const failing = data.problems.length > 0;
  const outline = failing ? 'var(--danger)' : selected ? 'var(--blue)' : 'var(--accent)';
  return (
    <HoverCard open={card !== null} content={card && <GateCardView card={card} />}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <span
          className="mono"
          style={{ fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}
        >
          {data.name}
        </span>
        <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
          <Handle type="target" position={Position.Left} style={WAY_IN} />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 7,
              transform: 'rotate(45deg)',
              borderRadius: 5,
              border: `1.5px solid ${outline}`,
              background: failing ? 'var(--danger-tint)' : 'var(--surface-card)',
              boxShadow: selected ? '0 0 0 3px var(--blue-tint)' : 'none',
            }}
          />
          <Handle type="source" position={Position.Right} isConnectable={false} style={ANCHOR} />
        </div>
        <span className="mono" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-soft)' }}>
          {data.mode === 'switch' ? 'switch gate' : 'if-else gate'}
        </span>
        {!data.follows && (
          <span
            style={{
              padding: '2px 7px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--warn-tint)',
              color: 'var(--warn-ink)',
              fontSize: 'var(--text-meta)',
              fontWeight: 'var(--weight-semibold)',
              whiteSpace: 'nowrap',
            }}
          >
            follows nothing yet
          </span>
        )}
        {failing && (
          <span style={{ color: 'var(--danger)', fontSize: 'var(--text-meta)' }}>
            {data.problems.length} {data.problems.length === 1 ? 'problem' : 'problems'}
          </span>
        )}
      </div>
    </HoverCard>
  );
}
